import {
	IModel,
	db,
	IIdentifiable,
	enumOrDefault,
	VIPCompany,
	IVIPCompany,
	IVIPCompanyOptions,
} from "@/loader";

export enum VipContactCivility {
	mr = 0,
	mrs = 1,
	ms = 2,
}

export function vipContactCivilityOrDefault(value: any, defaultValue: VipContactCivility = VipContactCivility.mr): VipContactCivility {
	return <VipContactCivility>enumOrDefault(value, defaultValue, Object.values(VipContactCivility));
}

export function getVipContactCivilityLabel(value: VipContactCivility): string {
	switch (value) {
		case VipContactCivility.mr:
			return window.wp.i18n._x("Mr", "Contact", "vip-vue");
		case VipContactCivility.mrs:
			return window.wp.i18n._x("Mrs", "Contact", "vip-vue");
		case VipContactCivility.ms:
			return window.wp.i18n._x("Ms", "Contact", "vip-vue");
		default:
			return window.wp.i18n._x("Unknown Civility", "Contact", "vip-vue");
	}
}

export function getVipContactCivilityList(): { value: any, text: string }[] {
	return [
		{ value: 0, text: window.wp.i18n._x("Mr", "Contact", "vip-vue") },
		{ value: 1, text: window.wp.i18n._x("Mrs", "Contact", "vip-vue") },
		{ value: 2, text: window.wp.i18n._x("Ms", "Contact", "vip-vue") },
	];
}

export type VIPContactFields = "id" | "fk_company" | "civility" | "phone" | "last_name" | "first_name" | "email" | "position";

/**
 * Contact Base Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPContactBase extends IIdentifiable {
	/** Id */
	id?: number;
	/** Company ID */
	fk_company?: number;
	/** Phone */
	phone?: string;
	/** Last name */
	last_name?: string;
	/** First name */
	first_name?: string;
	/** Email */
	email?: string;
	/** Position */
	position?: string;
	/** Civility */
	civility?: VipContactCivility;

	/** Company */
	company?: IVIPCompany | null;
}

/**
 * Contact Base Options Interface.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export interface IVIPContactOptions {
	/** Indicates whether the Id should be included or not. Default true. */
	id?: boolean;
	/** Indicates whether the Company ID should be included or not. Default true. */
	fk_company?: boolean;
	/** Indicates whether the Phone should be included or not. Default true. */
	phone?: boolean;
	/** Indicates whether the Last name should be included or not. Default true. */
	last_name?: boolean;
	/** Indicates whether the First name should be included or not. Default true. */
	first_name?: boolean;
	/** Indicates whether the Email should be included or not. Default true. */
	email?: boolean;
	/** Indicates whether the Position should be included or not. Default true. */
	position?: boolean;
	/** Indicates whether the Civility should be included or not. Default true. */
	civility?: boolean;
	/** Indicates whether the Company should be included or not. Default false. */
	company?: boolean | IVIPCompanyOptions;
}

/**
 * Contact Base Class Logic.
 *
 * Auto-generated by WP Generator (copyright 86Dev).
 * Do not modify it because it will be overwritten by WP Generator.
 *
 * @author		86Dev
 * @version		1.2.2
 * @since		1.0.0
 */
export class VIPContactBase implements IVIPContactBase, IModel<IVIPContactBase> {
	/** Id */
	public id!: number;
	/** Company ID */
	public fk_company!: number;
	/** Phone */
	public phone!: string;
	/** Last name */
	public last_name!: string;
	/** First name */
	public first_name!: string;
	/** Email */
	public email!: string;
	/** Position */
	public position!: string;
	/** Civility */
	public civility!: VipContactCivility;

	/**
	 * Get Company
	 */
	public get company(): VIPCompany | null {
		return db.companies.get(this.fk_company);
	}

	/**
	 * Set Company
	 */
	public set company(value: VIPCompany | null) {
		this.fk_company = Number.orDefault(value?.id);
	}

	/**
	 * Check if this Civility is Mr.
	 *
	 * @return bool
	 */
	public get isCivilityMr(): boolean {
		return this.civility === VipContactCivility.mr;
	}

	/**
	 * Check if this Civility is Mrs.
	 *
	 * @return bool
	 */
	public get isCivilityMrs(): boolean {
		return this.civility === VipContactCivility.mrs;
	}

	/**
	 * Check if this Civility is Ms.
	 *
	 * @return bool
	 */
	public get isCivilityMs(): boolean {
		return this.civility === VipContactCivility.ms;
	}

	/**
	 * Get the Civility label
	 *
	 * @return string
	 */
	public getCivilityLabel(): string {
		return getVipContactCivilityLabel(this.civility);
	}

	/**
	 * Constructor.
	 * @param data
	 */
	constructor (data: IVIPContactBase = {}) {
		this.load(data);
	}

	/**
	 * Set all properties with the data provided or a default value.
	 *
	 * All properties will be set, either with the value provided or with their default value.
	 *
	 * If you want to set only a subset of this instance's properties, use {@link VIPContactBase.update} instead.
	 *
	 * @param data
	 */
	public load(data: IVIPContactBase) {
		this.id = Number.orDefault(data.id);
		this.fk_company = Number.orDefault(data.fk_company);
		this.phone = String.orDefault(data.phone);
		this.last_name = String.orDefault(data.last_name);
		this.first_name = String.orDefault(data.first_name);
		this.email = String.orDefault(data.email);
		this.position = String.orDefault(data.position);
		this.civility = vipContactCivilityOrDefault(data.civility);
		if (data.company) {
			db.companies.insertOrUpdate(data.company);
		}
	}

	/**
	 * Set the properties with the data provided.
	 *
	 * Only the properties defined in data will be updated. Falsy values will be treated, only `undefined` is not.
	 *
	 * If you want to redefine all properties, use {@link VIPContactBase.load} instead.
	 *
	 * @param data
	 */
	public update(data: IVIPContactBase) {
		if (data.id !== undefined) {
			this.id = Number.orDefault(data.id);
		}
		if (data.fk_company !== undefined) {
			this.fk_company = Number.orDefault(data.fk_company);
		}
		if (data.phone !== undefined) {
			this.phone = String.orDefault(data.phone);
		}
		if (data.last_name !== undefined) {
			this.last_name = String.orDefault(data.last_name);
		}
		if (data.first_name !== undefined) {
			this.first_name = String.orDefault(data.first_name);
		}
		if (data.email !== undefined) {
			this.email = String.orDefault(data.email);
		}
		if (data.position !== undefined) {
			this.position = String.orDefault(data.position);
		}
		if (data.civility !== undefined) {
			this.civility = vipContactCivilityOrDefault(data.civility);
		}
		if (data.company) {
			db.companies.insertOrUpdate(data.company);
		}
	}
}
